import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { authDisabled, getWorkflowAccessToken, isAuthConfigured, loginRequest, msal, workflowEnabled, photoLibraryEnabled } from "./auth";
import WorkflowPanel from "./WorkflowPanel";
import DirectUpdaterWorkspace from "./DirectUpdaterWorkspace";
import PortalNavigation from "./PortalNavigation";
import { edgeSelection, refinePersonConfidence } from "./mask-tools";
import { assessPortraitPlacement, calculateAutoPlacement } from "./portrait-placement";
import { detectRenderedShoulderCrop } from "./shoulder-crop";
import { mergePortraitAlpha, removeFlatFringe } from "./portrait-mask";
import { getImageProcessingProfile } from "./image-processing-compat";

const WIDTH = 605;
const HEIGHT = 688;
const SHOULDER_CROP_WARNING_TEXT = "Warning: we have detected your photo is cropped from the shoulders and may not be accepted. Would you still like to proceed?";
const GUIDE_Y = 63;
const ARTWORK_SOURCE_SIZE = 950;
const MEDIAPIPE_WASM_PATH = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const PERSON_MODEL_PATH = "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite";
const CLOTHING_MODEL_PATH = "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite";
const FACE_MODEL_PATH = "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/latest/blaze_face_short_range.tflite";
// Local-only experimental engine. Set VITE_MATTING_ENGINE=isnet to force the
// existing @imgly/IS-Net path while comparing results.
const MATTING_ENGINE = import.meta.env.VITE_MATTING_ENGINE || "birefnet";
const IMAGE_PROCESSING_PROFILE = getImageProcessingProfile(typeof navigator === "undefined" ? {} : navigator);

let visionRuntimePromise;
let personSegmenterPromise;
let clothingSegmenterPromise;
let faceDetectorPromise;

function smoothStep(edge0, edge1, value) {
  const amount = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return amount * amount * (3 - 2 * amount);
}


async function visionRuntime() {
  if (!visionRuntimePromise) {
    visionRuntimePromise = import("@mediapipe/tasks-vision")
      .then(async (mediaPipe) => ({
        mediaPipe,
        fileset: await mediaPipe.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH),
      }))
      .catch((error) => {
        visionRuntimePromise = null;
        throw error;
      });
  }
  return visionRuntimePromise;
}

async function personSegmenter() {
  if (!personSegmenterPromise) {
    personSegmenterPromise = visionRuntime()
      .then(({ mediaPipe, fileset }) => mediaPipe.ImageSegmenter.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: PERSON_MODEL_PATH, delegate: "CPU" },
        runningMode: "IMAGE",
        outputConfidenceMasks: true,
        outputCategoryMask: false,
      }))
      .catch((error) => {
        personSegmenterPromise = null;
        throw error;
      });
  }
  return personSegmenterPromise;
}

async function faceDetector() {
  if (!faceDetectorPromise) {
    faceDetectorPromise = visionRuntime()
      .then(({ mediaPipe, fileset }) => mediaPipe.FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: FACE_MODEL_PATH, delegate: "CPU" },
        runningMode: "IMAGE",
        minDetectionConfidence: 0.55,
      }))
      .catch((error) => {
        faceDetectorPromise = null;
        throw error;
      });
  }
  return faceDetectorPromise;
}

async function detectLargestFace(canvas) {
  const result = await (await faceDetector()).detect(canvas);
  const detections = Array.isArray(result?.detections) ? result.detections : [];
  const detection = detections
    .filter((item) => item?.boundingBox?.width > 0 && item?.boundingBox?.height > 0)
    .sort((left, right) => (right.boundingBox.width * right.boundingBox.height) - (left.boundingBox.width * left.boundingBox.height))[0];
  if (!detection) return null;
  const box = detection.boundingBox;
  return { x: box.originX, y: box.originY, width: box.width, height: box.height };
}

function alphaBounds(canvas) {
  const context = canvas?.getContext("2d");
  if (!context) return null;
  const { width, height } = canvas;
  const pixels = context.getImageData(0, 0, width, height).data;
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    if (pixels[(y * width + x) * 4 + 3] < 24) continue;
    left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y);
  }
  return right < left ? null : { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

async function supplementMaskWithPersonModel(originalCanvas, maskCanvas) {
  const segmenter = await personSegmenter();
  const result = segmenter.segment(originalCanvas);
  let clothingResult;
  try {
    const confidenceMask = result.confidenceMasks?.[0];
    if (!confidenceMask) return null;
    let coarseConfidence = new Float32Array(confidenceMask.getAsFloat32Array());
    // Keep the existing portrait model as a fallback and as a second opinion
    // for head coverings. Multiclass channel zero is background, not person.
    try {
      clothingSegmenterPromise ||= visionRuntime().then(({ mediaPipe, fileset }) => mediaPipe.ImageSegmenter.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: CLOTHING_MODEL_PATH, delegate: "CPU" },
        runningMode: "IMAGE", outputConfidenceMasks: true, outputCategoryMask: false,
      })).catch((error) => { clothingSegmenterPromise = null; throw error; });
      clothingResult = (await clothingSegmenterPromise).segment(originalCanvas);
      const background = clothingResult.confidenceMasks?.[0];
      if (background) {
        const values = background.getAsFloat32Array();
        const combined = new Float32Array(coarseConfidence);
        for (let y = 0; y < confidenceMask.height; y++) for (let x = 0; x < confidenceMask.width; x++) {
          const index = y * confidenceMask.width + x;
          const bx = Math.min(background.width - 1, Math.floor((x + .5) * background.width / confidenceMask.width));
          const by = Math.min(background.height - 1, Math.floor((y + .5) * background.height / confidenceMask.height));
          combined[index] = Math.max(combined[index], 1 - values[by * background.width + bx]);
        }
        coarseConfidence = combined;
      }
    } catch (error) { console.warn("Clothing enhancement unavailable; retaining portrait enhancement", error); }
    const guide = document.createElement("canvas");
    const ratio = Math.min(1, 768 / Math.max(originalCanvas.width, originalCanvas.height));
    guide.width = Math.max(1, Math.round(originalCanvas.width * ratio));
    guide.height = Math.max(1, Math.round(originalCanvas.height * ratio));
    const guideContext = guide.getContext("2d");
    guideContext.drawImage(originalCanvas, 0, 0, guide.width, guide.height);
    const confidence = refinePersonConfidence(guideContext.getImageData(0, 0, guide.width, guide.height).data, guide.width, guide.height, coarseConfidence, confidenceMask.width, confidenceMask.height);
    const automaticCanvas = document.createElement("canvas");
    const smartCanvas = document.createElement("canvas");
    automaticCanvas.width = smartCanvas.width = guide.width;
    automaticCanvas.height = smartCanvas.height = guide.height;
    const automaticContext = automaticCanvas.getContext("2d");
    const smartContext = smartCanvas.getContext("2d");
    if (!automaticContext || !smartContext) return null;
    const automaticPixels = automaticContext.createImageData(automaticCanvas.width, automaticCanvas.height);
    const smartPixels = smartContext.createImageData(smartCanvas.width, smartCanvas.height);
    for (let index = 0; index < confidence.length; index += 1) {
      const offset = index * 4;
      automaticPixels.data[offset] = smartPixels.data[offset] = 255;
      automaticPixels.data[offset + 1] = smartPixels.data[offset + 1] = 255;
      automaticPixels.data[offset + 2] = smartPixels.data[offset + 2] = 255;
      // White clothing and a white wall are deliberately conservative here:
      // medium-confidence person pixels must not grow the cutout halo. The
      // original IS-Net alpha remains underneath, while only high-confidence
      // MediaPipe pixels can restore a missing garment edge.
      automaticPixels.data[offset + 3] = Math.round(smoothStep(0.72, 0.94, confidence[index]) * 255);
      smartPixels.data[offset + 3] = Math.round(smoothStep(0.42, 0.82, confidence[index]) * 255);
    }
    automaticContext.putImageData(automaticPixels, 0, 0);
    smartContext.putImageData(smartPixels, 0, 0);
    const maskContext = maskCanvas.getContext("2d");
    if (!maskContext) return null;
    const fullPerson = document.createElement("canvas");
    fullPerson.width = maskCanvas.width;
    fullPerson.height = maskCanvas.height;
    const fullContext = fullPerson.getContext("2d");
    fullContext.drawImage(automaticCanvas, 0, 0, fullPerson.width, fullPerson.height);
    const basePixels = maskContext.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    mergePortraitAlpha(basePixels.data, fullContext.getImageData(0, 0, fullPerson.width, fullPerson.height).data);
    const alpha = new Uint8ClampedArray(maskCanvas.width * maskCanvas.height);
    for (let index = 0; index < alpha.length; index += 1) alpha[index] = basePixels.data[index * 4 + 3];
    const sourcePixels = originalCanvas.getContext("2d").getImageData(0, 0, originalCanvas.width, originalCanvas.height).data;
    removeFlatFringe(sourcePixels, alpha, maskCanvas.width, maskCanvas.height);
    for (let index = 0; index < alpha.length; index += 1) basePixels.data[index * 4 + 3] = alpha[index];
    maskContext.putImageData(basePixels, 0, 0);
    return smartCanvas;
  } finally {
    clothingResult?.close();
    result.close();
  }
}

function imageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image could not be opened"));
    };
    image.src = url;
  });
}

function backgroundRemovalErrorMessage(error) {
  const detail = error instanceof Error ? error.message : String(error);
  if (/fetch|network|resource|size/i.test(detail)) {
    return "The background-removal model could not be downloaded. Check your connection or ask IT to allow huggingface.co and staticimgly.com, then try again.";
  }
  if (/webassembly|wasm|content security|unsafe-eval|blob/i.test(detail)) {
    return "This browser blocked the background-removal engine. Refresh the page and try again, or contact IT if the problem continues.";
  }
  return `Background removal didn’t finish. ${detail || "Try a smaller JPG or PNG."}`;
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Mask export failed")), "image/png");
  });
}

function AuthGate({ children }) {
  const [status, setStatus] = useState(authDisabled ? "authenticated" : "loading");
  const [account, setAccount] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authDisabled || !isAuthConfigured || !msal) {
      setStatus(authDisabled ? "authenticated" : "configuration");
      return;
    }

    let active = true;
    (async () => {
      try {
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        const signedIn = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
        if (signedIn) msal.setActiveAccount(signedIn);
        if (active) {
          setAccount(signedIn);
          setStatus(signedIn ? "authenticated" : "anonymous");
        }
      } catch (authError) {
        if (active) {
          setError(authError instanceof Error ? authError.message : String(authError));
          setStatus("anonymous");
        }
      }
    })();
    return () => { active = false; };
  }, []);

  async function signIn() {
    setError("");
    try {
      await msal.loginRedirect(loginRequest);
    } catch (authError) {
      setError(authError instanceof Error ? authError.message : String(authError));
    }
  }

  async function signOut() {
    await msal.logoutRedirect({ account });
  }

  if (status === "loading") {
    return <AuthScreen title="Checking your Rawabi account…" busy />;
  }
  if (status === "configuration") {
    return (
      <AuthScreen
        title="Entra configuration required"
        description="The application is ready, but its Microsoft Entra application ID has not been added yet."
        error="Add VITE_ENTRA_CLIENT_ID to the deployment environment."
      />
    );
  }
  if (status === "anonymous") {
    return (
      <AuthScreen
        title="Employee sign in"
        description="Use your Rawabi Microsoft account to access the Profile Picture Studio."
        action="Sign in with Microsoft"
        onAction={signIn}
        error={error}
      />
    );
  }

  return children({
    account,
    signOut: authDisabled ? null : signOut,
    getAccessToken: authDisabled ? async () => { throw new Error("Workflow authentication is unavailable while local authentication is disabled."); } : () => getWorkflowAccessToken(account),
  });
}

function AuthScreen({ title, description, action, onAction, busy, error }) {
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <img className="auth-logo" src="/rawabi-commercial-fullcolor.png" alt="Rawabi" />
        <p className="eyebrow">Human Capital</p>
        <h1>{title}</h1>
        {description && <p className="auth-description">{description}</p>}
        {action && <button className="auth-button" onClick={onAction}>{action}</button>}
        {busy && <div className="auth-security"><span />Secure sign-in</div>}
        {error && <p className="auth-error">{error}</p>}
      </section>
    </main>
  );
}

export function Studio({ account, signOut, getAccessToken, directUpdateMode = false }) {
  const processingIdRef = useRef(0);
  const sourceCanvasRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const dragRef = useRef(null);
  const brushRef = useRef(null);
  const originalRef = useRef(null);
  const maskRef = useRef(null);
  const maskPixelsRef = useRef(null);
  const originalMaskRef = useRef(null);
  const compositeRef = useRef(null);

  const [photo, setPhoto] = useState(null);
  const [compositeRevision, refreshComposite] = useState(0);
  const [artwork, setArtwork] = useState(null);
  const [filename, setFilename] = useState("");
  const [photoInstance, setPhotoInstance] = useState("");
  const [sourceFile, setSourceFile] = useState(null);
  const [processing, setProcessing] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [processingError, setProcessingError] = useState("");
  const [enhancementUsed, setEnhancementUsed] = useState(false);
  const [mattingEngine, setMattingEngine] = useState("");
  const [smartStatus, setSmartStatus] = useState("idle");
  const [tool, setTool] = useState("photo");
  const [photoX, setPhotoX] = useState(WIDTH / 2);
  const [photoY, setPhotoY] = useState(HEIGHT / 2);
  const [photoScale, setPhotoScale] = useState(1);
  const [artworkX, setArtworkX] = useState(-260);
  const [artworkY, setArtworkY] = useState(310);
  const [artworkScale, setArtworkScale] = useState(0.61);
  const [brushSize, setBrushSize] = useState(34);
  const [brushCursor, setBrushCursor] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [faceBox, setFaceBox] = useState(null);
  const [foregroundBounds, setForegroundBounds] = useState(null);
  const [shoulderMaskRevision, setShoulderMaskRevision] = useState(0);
  const [shoulderDialogOpen, setShoulderDialogOpen] = useState(false);
  const shoulderDialogResolver = useRef(null);
  const [autoPositionStatus, setAutoPositionStatus] = useState("idle");

  const baseScale = photo ? Math.max(WIDTH / photo.width, HEIGHT / photo.height) : 1;
  const artworkGeometry = useMemo(() => ({ x: artworkX, y: artworkY, size: ARTWORK_SOURCE_SIZE * artworkScale }), [artworkScale, artworkX, artworkY]);
  const placementAssessment = useMemo(() => assessPortraitPlacement({
    imageWidth: photo?.width,
    imageHeight: photo?.height,
    baseScale,
    photoScale,
    photoX,
    photoY,
    face: faceBox,
    foreground: foregroundBounds,
    artwork: artworkGeometry,
  }), [artworkGeometry, baseScale, faceBox, foregroundBounds, photo, photoScale, photoX, photoY]);

  // Only flag straight edges visibly inset from the finished image frame.
  const shoulderCrop = useMemo(() => {
    if (!photo) return null;
    return detectRenderedShoulderCrop({
      pixels: maskPixelsRef.current, width: photo.width, height: photo.height,
      face: faceBox, foreground: foregroundBounds,
      scale: baseScale * photoScale, photoX, photoY,
      canvasWidth: WIDTH, canvasHeight: HEIGHT,
    });
  }, [photo, faceBox, foregroundBounds, baseScale, photoScale, photoX, photoY,
      shoulderMaskRevision]);

  const closeShoulderDialog = useCallback((proceed) => {
    const resolve = shoulderDialogResolver.current;
    shoulderDialogResolver.current = null;
    setShoulderDialogOpen(false);
    if (resolve) resolve(proceed);
  }, []);

  const confirmShoulderCrop = useCallback(() => {
    if (!shoulderCrop?.warning) return Promise.resolve(true);
    return new Promise((resolve) => {
      if (shoulderDialogResolver.current) shoulderDialogResolver.current(false);
      shoulderDialogResolver.current = resolve;
      setShoulderDialogOpen(true);
    });
  }, [shoulderCrop]);

  useEffect(() => () => {
    if (shoulderDialogResolver.current) shoulderDialogResolver.current(false);
    shoulderDialogResolver.current = null;
  }, []);

  useEffect(() => {
    const overlay = new Image();
    overlay.onload = () => setArtwork(overlay);
    overlay.src = "/brand-overlay.png";
  }, []);

  const drawCanvas = useCallback((canvas, exportMode = false) => {
    const context = canvas.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, WIDTH, HEIGHT);
    context.fillStyle = "#8d939e";
    context.fillRect(0, 0, WIDTH, HEIGHT);

    if (photo) {
      const scale = baseScale * photoScale;
      const width = photo.width * scale;
      const height = photo.height * scale;
      context.drawImage(photo, photoX - width / 2, photoY - height / 2, width, height);
    }

    if (!exportMode) {
      context.save();
      context.strokeStyle = "rgba(61, 75, 94, .62)";
      context.lineWidth = 1;
      context.setLineDash([4, 4]);
      context.beginPath();
      context.moveTo(0, GUIDE_Y + 0.5);
      context.lineTo(WIDTH, GUIDE_Y + 0.5);
      context.stroke();

      context.strokeStyle = "rgba(238, 213, 24, .92)";
      context.lineWidth = 1.5;
      context.setLineDash([]);
      context.beginPath();
      context.moveTo(WIDTH / 2 + 0.5, 0);
      context.lineTo(WIDTH / 2 + 0.5, HEIGHT);
      context.stroke();
      context.restore();
    }

    if (artwork) {
      const size = ARTWORK_SOURCE_SIZE * artworkScale;
      context.drawImage(artwork, artworkX, artworkY, size, size);
    }

    if (!exportMode && photo) {
      context.save();
      context.strokeStyle = tool === "artwork" ? "rgba(255,255,255,.82)" : "rgba(31,79,66,.72)";
      context.lineWidth = 1.5;
      context.setLineDash([6, 5]);
      if (tool === "artwork") {
        context.strokeRect(artworkX, artworkY, ARTWORK_SOURCE_SIZE * artworkScale, ARTWORK_SOURCE_SIZE * artworkScale);
      } else {
        const scale = baseScale * photoScale;
        context.strokeRect(
          photoX - (photo.width * scale) / 2,
          photoY - (photo.height * scale) / 2,
          photo.width * scale,
          photo.height * scale,
        );
      }
      context.restore();
    }

    if (!exportMode && photo && brushCursor && (tool === "restore" || tool === "erase")) {
      context.save();
      context.fillStyle = tool === "restore" ? "rgba(58,204,186,.14)" : "rgba(240,160,64,.14)";
      context.strokeStyle = "rgba(255,255,255,.95)";
      context.lineWidth = 3;
      context.setLineDash([]);
      context.beginPath();
      context.arc(brushCursor.x, brushCursor.y, brushSize / 2, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      context.strokeStyle = "rgba(41,52,70,.9)";
      context.lineWidth = 1;
      context.stroke();
      context.restore();
    }
  }, [artwork, artworkScale, artworkX, artworkY, baseScale, brushCursor, brushSize, compositeRevision, photo, photoScale, photoX, photoY, tool]);

  useEffect(() => {
    if (canvasRef.current) drawCanvas(canvasRef.current);
  }, [drawCanvas]);

  useEffect(() => {
    if (tool !== "restore" && tool !== "erase") setBrushCursor(null);
  }, [tool]);

  function resetPlacement() {
    setPhotoX(WIDTH / 2);
    setPhotoY(HEIGHT / 2);
    setPhotoScale(0.94);
    setArtworkX(-260);
    setArtworkY(310);
    setArtworkScale(0.61);
  }

  function autoPositionFace() {
    if (!photo || !faceBox) return;
    const placement = calculateAutoPlacement({
      imageWidth: photo.width,
      imageHeight: photo.height,
      baseScale,
      face: faceBox,
      foreground: foregroundBounds,
      artwork: artworkGeometry,
    });
    if (!placement) return;
    setPhotoX(placement.photoX);
    setPhotoY(placement.photoY);
    setPhotoScale(placement.photoScale);
    if (placement.artworkX != null) setArtworkX(placement.artworkX);
    if (placement.artworkY != null) setArtworkY(placement.artworkY);
    setTool("photo");
    setAutoPositionStatus("positioned");
  }

  function updateShoulderMask(mask) {
    // Cache raw person alpha for the straight-cut shape test. Do not judge
    // side contact in the original photo or include the turquoise artwork.
    maskPixelsRef.current = mask?.getContext("2d")
      ?.getImageData(0, 0, mask.width, mask.height).data || null;
    setShoulderMaskRevision((value) => value + 1);
  }

  function rebuildComposite(updateBounds = false) {
    const original = originalRef.current;
    const mask = maskRef.current;
    const composite = compositeRef.current;
    if (!original || !mask || !composite) return;
    const context = composite.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, composite.width, composite.height);
    context.globalCompositeOperation = "source-over";
    context.drawImage(original, 0, 0, composite.width, composite.height);
    context.globalCompositeOperation = "destination-in";
    context.drawImage(mask, 0, 0);
    context.globalCompositeOperation = "source-over";
    refreshComposite((value) => value + 1);
    if (updateBounds) setForegroundBounds(alphaBounds(mask));
  }

  function resetCutout() {
    const mask = maskRef.current;
    const originalMask = originalMaskRef.current;
    if (!mask || !originalMask) return;
    const context = mask.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, mask.width, mask.height);
    context.drawImage(originalMask, 0, 0);
    setSmartStatus("idle");
    rebuildComposite(true);
    updateShoulderMask(mask);
  }

  function brushAt(x, y) {
    if (!photo || (tool !== "restore" && tool !== "erase")) return false;
    const scale = baseScale * photoScale;
    const sourceX = (x - (photoX - (photo.width * scale) / 2)) / scale;
    const sourceY = (y - (photoY - (photo.height * scale) / 2)) / scale;
    if (sourceX < 0 || sourceY < 0 || sourceX > photo.width || sourceY > photo.height) return false;

    const radius = Math.max(2, brushSize / (2 * scale));
    const mask = maskRef.current;
    const context = mask?.getContext("2d");
    if (!mask || !context) return false;
    const source = sourceCanvasRef.current?.getContext("2d");
    if (!source) return false;
    const left = Math.max(0, Math.floor(sourceX - radius));
    const top = Math.max(0, Math.floor(sourceY - radius));
    const width = Math.min(mask.width - left, Math.ceil(sourceX + radius) - left + 1);
    const height = Math.min(mask.height - top, Math.ceil(sourceY + radius) - top + 1);
    if (width <= 0 || height <= 0) return false;
    const pixels = source.getImageData(left, top, width, height);
    const selection = edgeSelection(pixels.data, width, height, sourceX - left, sourceY - top, radius);
    const maskPixels = context.getImageData(left, top, width, height);
    for (let i = 0; i < selection.length; i++) {
      const alpha = i * 4 + 3;
      maskPixels.data[alpha] = tool === "restore"
        ? Math.round(maskPixels.data[alpha] + (255 - maskPixels.data[alpha]) * selection[i])
        : Math.round(maskPixels.data[alpha] * (1 - selection[i]));
    }
    context.putImageData(maskPixels, left, top);
    return true;
  }


  async function processFile(file) {
    if (!file || !file.type.startsWith("image/")) return;
    const processingId = ++processingIdRef.current;
    const isCurrent = () => processingId === processingIdRef.current;
    setSourceFile(file);
    setFilename(file.name);
    setPhoto(null);
    setEnhancementUsed(false);
    setMattingEngine("");
    setFaceBox(null);
    setForegroundBounds(null);
    maskPixelsRef.current = null;
    setAutoPositionStatus("detecting");
    setSmartStatus("idle");
    setProcessing("processing");
    setProgress(2);
    setProcessingError("");

    try {
      if (file.size > 25 * 1024 * 1024) throw new Error("Please use an image smaller than 25 MB.");
      let input = file;
      const inputImage = await imageFromBlob(file);
      if (!isCurrent()) return;
      const sourceMaxDimension = Math.max(inputImage.naturalWidth, inputImage.naturalHeight);
      if (sourceMaxDimension > IMAGE_PROCESSING_PROFILE.maxDimension) {
        const canvas = document.createElement("canvas");
        const scale = IMAGE_PROCESSING_PROFILE.maxDimension / sourceMaxDimension;
        canvas.width = Math.max(1, Math.round(inputImage.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(inputImage.naturalHeight * scale));
        const resizeContext = canvas.getContext("2d");
        if (!resizeContext) throw new Error("Canvas is not available");
        resizeContext.drawImage(inputImage, 0, 0, canvas.width, canvas.height);
        input = await new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Image resize failed")), "image/jpeg", 0.92));
        resizeContext.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 1;
        canvas.height = 1;
      }
      inputImage.src = "";
      if (IMAGE_PROCESSING_PROFILE.lowMemoryMode) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      if (!isCurrent()) return;
      let cutoutBlob;
      let engineLabel = "IS-Net";
      if (MATTING_ENGINE === "birefnet" && IMAGE_PROCESSING_PROFILE.useBiRefNet) {
        try {
          setProgress(8);
          const { removeBackgroundWithBiRefNet } = await import("./birefnet-removal");
          const alphaCanvas = await removeBackgroundWithBiRefNet(input, {
            onProgress: (event) => {
              const value = typeof event === "number" ? event : Number(event?.progress);
              const normalized = value > 1 ? value / 100 : value;
              if (isCurrent() && Number.isFinite(normalized)) setProgress(Math.max(8, Math.min(92, Math.round(normalized * 84))));
            },
          });
          cutoutBlob = await canvasToPngBlob(alphaCanvas);
          engineLabel = "BiRefNet lite matting";
        } catch (mattingError) {
          console.warn("BiRefNet matting unavailable; falling back to IS-Net", mattingError);
          engineLabel = "IS-Net fallback (BiRefNet unavailable)";
        }
      }
      if (!cutoutBlob) {
        const { removeBackground } = await import("@imgly/background-removal");
        if (!isCurrent()) return;
        cutoutBlob = await removeBackground(input, {
          device: "cpu",
          model: "isnet",
          output: { format: "image/png", quality: 1 },
          progress: (_key, current, total) => {
            if (isCurrent() && total > 0) setProgress(Math.max(3, Math.min(92, Math.round((current / total) * 92))));
          },
        });
      }
      if (!isCurrent()) return;
      setMattingEngine(engineLabel);
      setProgress(94);
      const [original, cutout] = await Promise.all([imageFromBlob(input), imageFromBlob(cutoutBlob)]);
      if (!isCurrent()) return;
      const width = cutout.naturalWidth;
      const height = cutout.naturalHeight;
      const mask = document.createElement("canvas");
      const originalMask = document.createElement("canvas");
      const composite = document.createElement("canvas");
      const originalCanvas = document.createElement("canvas");
      mask.width = originalMask.width = composite.width = width;
      mask.height = originalMask.height = composite.height = height;
      originalCanvas.width = width;
      originalCanvas.height = height;
      mask.getContext("2d")?.drawImage(cutout, 0, 0, width, height);
      const originalContext = originalCanvas.getContext("2d");
      if (!originalContext) throw new Error("Canvas is not available");
      originalContext.drawImage(original, 0, 0, width, height);
      setProgress(96);
      let personMask = null;
      if (engineLabel.startsWith("IS-Net") && IMAGE_PROCESSING_PROFILE.useSecondaryVisionModels) {
        try {
          personMask = await supplementMaskWithPersonModel(originalCanvas, mask);
        } catch (enhancementError) {
          console.warn("Person-mask enhancement was unavailable; using the IS-Net cutout:", enhancementError);
        }
      }
      if (!isCurrent()) return;
      setProgress(99);
      originalMask.getContext("2d")?.drawImage(mask, 0, 0);
      originalRef.current = original;
      sourceCanvasRef.current = originalCanvas;
      maskRef.current = mask;
      originalMaskRef.current = originalMask;
      compositeRef.current = composite;
      const context = composite.getContext("2d");
      if (!context) throw new Error("Canvas is not available");
      context.drawImage(original, 0, 0, width, height);
      context.globalCompositeOperation = "destination-in";
      context.drawImage(mask, 0, 0);
      context.globalCompositeOperation = "source-over";
      const bounds = alphaBounds(mask);
      let detectedFace = null;
      if (IMAGE_PROCESSING_PROFILE.useSecondaryVisionModels) {
        try {
          detectedFace = await detectLargestFace(originalCanvas);
        } catch (faceError) {
          console.warn("Automatic face positioning was unavailable:", faceError);
        }
      }
      if (!isCurrent()) return;
      setPhoto(composite);
      setPhotoInstance(crypto.randomUUID());
      setEnhancementUsed(Boolean(personMask));
      setFaceBox(detectedFace);
      setForegroundBounds(bounds);
      updateShoulderMask(mask);
      const initialBaseScale = Math.max(WIDTH / width, HEIGHT / height);
      const initialPlacement = calculateAutoPlacement({
        imageWidth: width,
        imageHeight: height,
        baseScale: initialBaseScale,
        face: detectedFace,
        foreground: bounds,
        artwork: { x: -260, y: 310, size: ARTWORK_SOURCE_SIZE * 0.61 },
      });
      if (initialPlacement) {
        setPhotoX(initialPlacement.photoX);
        setPhotoY(initialPlacement.photoY);
        setPhotoScale(initialPlacement.photoScale);
        if (initialPlacement.artworkX != null) setArtworkX(initialPlacement.artworkX);
        if (initialPlacement.artworkY != null) setArtworkY(initialPlacement.artworkY);
        setArtworkScale(0.61);
        setAutoPositionStatus("positioned");
      } else {
        resetPlacement();
        setAutoPositionStatus("unavailable");
      }
      setTool("photo");
      setProgress(100);
      setProcessing("ready");
    } catch (processError) {
      if (!isCurrent()) return;
      console.error("Background removal failed:", processError);
      setProcessing("error");
      setProcessingError(backgroundRemovalErrorMessage(processError));
    }
  }

  function canvasPoint(event) {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) * WIDTH) / bounds.width,
      y: ((event.clientY - bounds.top) * HEIGHT) / bounds.height,
    };
  }

  function pointerDown(event) {
    if (!photo) return;
    const point = canvasPoint(event);
    if (tool === "restore" || tool === "erase") {
      setBrushCursor(point);
      brushRef.current = point;
      event.currentTarget.setPointerCapture(event.pointerId);
      if (brushAt(point.x, point.y)) rebuildComposite();
      return;
    }
    dragRef.current = {
      x: point.x,
      y: point.y,
      startX: tool === "photo" ? photoX : artworkX,
      startY: tool === "photo" ? photoY : artworkY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  }

  function pointerMove(event) {
    const point = canvasPoint(event);
    if (tool === "restore" || tool === "erase") setBrushCursor(point);
    if (brushRef.current && (tool === "restore" || tool === "erase")) {
      const previous = brushRef.current;
      const distance = Math.hypot(point.x - previous.x, point.y - previous.y);
      const steps = Math.max(1, Math.ceil(distance / Math.max(3, brushSize * 0.2)));
      let changed = false;
      for (let index = 1; index <= steps; index += 1) {
        const stepX = previous.x + ((point.x - previous.x) * index) / steps;
        const stepY = previous.y + ((point.y - previous.y) * index) / steps;
        changed = brushAt(stepX, stepY) || changed;
      }
      if (changed) rebuildComposite();
      brushRef.current = point;
      return;
    }
    if (!dragRef.current) return;
    const dx = point.x - dragRef.current.x;
    const dy = point.y - dragRef.current.y;
    if (tool === "photo") {
      setPhotoX(dragRef.current.startX + dx);
      setPhotoY(dragRef.current.startY + dy);
    } else {
      setArtworkX(dragRef.current.startX + dx);
      setArtworkY(dragRef.current.startY + dy);
    }
  }

  function pointerUp() {
    if (brushRef.current && maskRef.current) {
      const bounds = alphaBounds(maskRef.current);
      setForegroundBounds(bounds);
      updateShoulderMask(maskRef.current);
    }
    dragRef.current = null;
    brushRef.current = null;
    setDragging(false);
    // The live edge-aware brush is the final result; never undo it on release.
  }

  function pointerLeave() {
    if (!brushRef.current) setBrushCursor(null);
  }

  function nudge(dx, dy) {
    if (tool === "photo") {
      setPhotoX((value) => value + dx);
      setPhotoY((value) => value + dy);
    } else {
      setArtworkX((value) => value + dx);
      setArtworkY((value) => value + dy);
    }
  }

  function renderOutputBlob() {
    if (!photo || !artwork) return Promise.reject(new Error("Prepare a finished photo first."));
    const output = document.createElement("canvas");
    output.width = WIDTH;
    output.height = HEIGHT;
    drawCanvas(output, true);
    return new Promise((resolve, reject) => output.toBlob((blob) => {
      if (!blob) { reject(new Error("The finished photo could not be created.")); return; }
      resolve(blob);
    }, "image/png"));
  }

  async function download() {
    if (!photo) return;
    try {
      const blob = await renderOutputBlob();
      const link = document.createElement("a");
      const safeName = filename.replace(/\.[^.]+$/, "").replace(/[^a-z0-9-_@.]+/gi, "-") || "employee";
      link.href = URL.createObjectURL(blob);
      link.download = `${safeName}-profile.png`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 500);
    } catch (error) {
      console.error("Profile image export failed:", error);
    }
  }

  const accountLabel = account?.name || account?.username;

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="rawabi-lockup"><img src="/rawabi-commercial-fullcolor.png" alt="Rawabi" /></div>
        <div className="product-lockup"><p className="eyebrow">Human Capital</p><h1>{directUpdateMode ? "Direct Update Workspace" : "Profile Picture Studio"}</h1></div>
        {accountLabel ? (
          <div className="account-menu">
            <div className="account-copy"><strong>{account?.name || "Rawabi employee"}</strong><span>{account?.username}</span></div>
            <PortalNavigation getAccessToken={getAccessToken} current={directUpdateMode ? "direct-update" : "studio"} />
            {signOut && <button className="signout-button" onClick={signOut}>Sign out</button>}
          </div>
        ) : <div className="privacy-pill"><span />Processed on this device</div>}
      </header>

      <section className="workspace portrait-workspace">
        <aside className="control-panel">
          <div className="panel-section upload-section">
            {photoLibraryEnabled && <p className="workflow-notice">Please note that submitting your image stores your untouched original photo and the final Microsoft 365 photo in a restricted photo library. Your original photo may contain camera metadata. Designated Human Capital and/or Corporate Communications employees may access both photos for business purposes.</p>}
            <div className="step-number">01</div><h2>Employee photo</h2>
            <p>Choose a clear portrait. The background will be removed automatically on this device.</p>
            <details className="placement-example">
              <summary>See the correct portrait position</summary>
              <img src="/portrait-placement-guide.svg" alt="Example showing the head below the guide and turquoise artwork clear of the face" />
              <p>Keep the full face inside the dashed area. The turquoise artwork may cover clothing, but must not touch the face.</p>
            </details>
            <input ref={fileRef} className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; processFile(file); }} />
            <button className="upload-button" disabled={processing === "processing"} onClick={() => fileRef.current?.click()}>
              <span>＋</span>{processing === "processing" ? "Removing background…" : photo ? "Replace photo" : "Choose photo"}
            </button>
            {processing === "processing" && <div className="processing-status" role="status" aria-live="polite"><div className="progress-track"><span style={{ width: `${progress}%` }} /></div><p>Preparing cutout · {progress}%</p><small>The first photo may take a little longer while the removal model loads.</small></div>}
            {processing === "ready" && <p className="file-name" title={filename}>✓ {mattingEngine || "High-quality local"} cutout ready · {filename}</p>}
            {photo && shoulderCrop?.warning && <div className="portrait-crop-warning" role="alert">
              <strong>⚠ Shoulder crop warning</strong>
              <p>{SHOULDER_CROP_WARNING_TEXT}</p>
              <button type="button" onClick={() => fileRef.current?.click()}>Replace photo</button>
            </div>}
            {processing === "error" && <div className="processing-error" role="alert"><p>{processingError}</p><button onClick={() => processFile(sourceFile)}>Try again</button></div>}
          </div>

          <div className={`panel-section adjustments ${photo ? "" : "is-disabled"}`}>
            <div className="step-number">02</div>
            <div className="section-title"><h2>Position &amp; refine</h2><div className="section-title-actions"><button onClick={autoPositionFace} disabled={!photo || !faceBox}>Auto position</button><button onClick={resetPlacement} disabled={!photo}>Reset</button></div></div>
            <label className="control-label">Edit</label>
            <div className="segmented edit-tools" role="group" aria-label="Choose editing tool">
              {[["photo", "Photo"], ["artwork", "Circle artwork"], ["restore", "Magic restore"], ["erase", "Erase"]].map(([value, label]) => <button key={value} className={tool === value ? "active" : ""} onClick={() => setTool(value)}>{label}</button>)}
            </div>
            {photo && <div className="placement-checks" role="status" aria-live="polite">
              <div className={placementAssessment.headroom ? "is-good" : "is-warning"}><span>{placementAssessment.headroom ? "✓" : "!"}</span><p><strong>Head position</strong>{placementAssessment.headroom ? "Below the guide line" : "Use Auto position to check the headroom"}</p></div>
              <div className={placementAssessment.artworkClear ? "is-good" : "is-warning"}><span>{placementAssessment.artworkClear ? "✓" : "!"}</span><p><strong>Face clearance</strong>{placementAssessment.artworkClear ? "Turquoise artwork is clear" : "Move the circle left or down"}</p></div>
              <div className={placementAssessment.framed ? "is-good" : "is-warning"}><span>{placementAssessment.framed ? "✓" : "!"}</span><p><strong>Face framing</strong>{placementAssessment.detected ? placementAssessment.framed ? "Face size looks consistent" : "Use Auto position" : "Face was not detected; use the sample guide"}</p></div>
              <div className={placementAssessment.coverage ? "is-good" : "is-warning"}><span>{placementAssessment.coverage ? "✓" : "!"}</span><p><strong>Portrait bottom</strong>{placementAssessment.coverage ? "Portrait reaches the bottom edge" : "Move the portrait down slightly or use Auto position"}</p></div>
              <div className={shoulderCrop?.warning ? "is-warning" : "is-good"}><span>{shoulderCrop?.warning ? "!" : "✓"}</span><p><strong>Shoulder framing</strong>{shoulderCrop?.warning ? "Straight vertical cutoff — review photo" : "No visible straight cutoff inside the photo frame"}</p></div>
              {autoPositionStatus === "positioned" && <small>Automatic face positioning applied. You can still fine-tune it.</small>}
              {placementAssessment.faceRect && <small>The face outline is a preview guide only; it is not included in downloads or submissions.</small>}
            </div>}
            {tool === "restore" || tool === "erase" ? <>
              <div className="cleanup-note"><strong>{tool === "restore" ? "Click missing clothing once" : "Remove remaining background"}</strong> {tool === "restore" ? "Magic Restore follows similar colours and stops at nearby edges. Click another separated area if needed." : "Brush over any background that remains."}</div>
              <label className="control-label" htmlFor="brush-size">{tool === "restore" ? "Magic reach" : "Brush size"} <span>{brushSize}px</span></label>
              <input id="brush-size" type="range" min="12" max="72" step="2" value={brushSize} onChange={(event) => setBrushSize(Number(event.target.value))} />
              <button className="reset-cutout" onClick={resetCutout}>Reset cutout</button>
            </> : <>
              <label className="control-label" htmlFor="scale">{tool === "photo" ? "Photo size" : "Artwork size"}<span>{Math.round((tool === "photo" ? photoScale : artworkScale / 0.61) * 100)}%</span></label>
              {tool === "photo" ? <input id="scale" type="range" min="0.2" max="3.5" step="0.01" value={photoScale} onChange={(event) => setPhotoScale(Number(event.target.value))} /> : <input id="scale" type="range" min="0.5" max="0.75" step="0.005" value={artworkScale} onChange={(event) => setArtworkScale(Number(event.target.value))} />}
              <div className="nudge-grid" aria-label={`Nudge ${tool}`}><button className="up" onClick={() => nudge(0, -2)} aria-label="Move up">↑</button><button className="left" onClick={() => nudge(-2, 0)} aria-label="Move left">←</button><span>{tool === "photo" ? "Photo" : "Artwork"}</span><button className="right" onClick={() => nudge(2, 0)} aria-label="Move right">→</button><button className="down" onClick={() => nudge(0, 2)} aria-label="Move down">↓</button></div>
            </>}
          </div>
          {workflowEnabled && (directUpdateMode
            ? <DirectUpdaterWorkspace photoReady={Boolean(photo && artwork)} originalPhoto={sourceFile} renderPhoto={renderOutputBlob} getAccessToken={getAccessToken} confirmShoulderCrop={confirmShoulderCrop} />
            : <WorkflowPanel photoReady={Boolean(photo && artwork)} originalPhoto={sourceFile} filename={`${filename}:${photoInstance}`} renderPhoto={renderOutputBlob} getAccessToken={getAccessToken} confirmShoulderCrop={confirmShoulderCrop} />)}
          <div className="format-note"><span>i</span><p>{workflowEnabled
            ? directUpdateMode
              ? "Background removal and editing run locally in this browser. The finished photo is uploaded only when an authorized Direct Updater confirms the Microsoft 365 update."
              : "Background removal and editing run locally in this browser. The finished photo is uploaded only when you choose Submit for approval."
            : "Background removal runs locally in this browser—employee photos are never uploaded. The grey background, guide bar and turquoise artwork are then added automatically."}</p></div>
        </aside>

        <section className="editor-card portrait-card" aria-label="Profile picture editor">
          <div className="editor-heading"><div><p className="eyebrow">Live preview</p><h2>{processing === "processing" ? "Removing the background" : photo ? tool === "restore" ? "Click missing clothing to restore it" : tool === "erase" ? "Brush remaining background away" : `Drag the ${tool === "photo" ? "photo" : "circle artwork"}` : "Position the portrait"}</h2></div><span className="dimensions">605 × 688 px</span></div>
          <div className={`canvas-wrap portrait-canvas ${dragging ? "dragging" : ""} ${tool === "restore" || tool === "erase" ? "brushing" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); processFile(event.dataTransfer.files?.[0]); }}>
            <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} tabIndex={photo ? 0 : -1} aria-label="Employee profile picture preview" onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={pointerUp} onPointerLeave={pointerLeave} onKeyDown={(event) => { if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) { event.preventDefault(); nudge(event.key === "ArrowLeft" ? -2 : event.key === "ArrowRight" ? 2 : 0, event.key === "ArrowUp" ? -2 : event.key === "ArrowDown" ? 2 : 0); } }} />
            {photo && placementAssessment.faceRect && tool !== "restore" && tool !== "erase" && <div className={`detected-face-guide ${placementAssessment.artworkClear && placementAssessment.framed ? "is-good" : "is-warning"}`} style={{ left: `${placementAssessment.faceRect.left / WIDTH * 100}%`, top: `${placementAssessment.faceRect.top / HEIGHT * 100}%`, width: `${placementAssessment.faceRect.width / WIDTH * 100}%`, height: `${placementAssessment.faceRect.height / HEIGHT * 100}%` }}><span>FACE</span></div>}
            <div className="guide-label portrait-guide">HEAD BELOW THIS LINE</div>
            {processing === "processing" ? <div className="canvas-processing" role="status"><span className="spinner" /><strong>Creating employee cutout</strong><small>{progress}% complete</small></div> : !photo && <button className="empty-prompt" onClick={() => fileRef.current?.click()}><strong>Drop a photo here</strong><span>or click to browse</span></button>}
          </div>
          <div className="editor-footer"><div className={`placement-guide ${photo && placementAssessment.good && !shoulderCrop?.warning ? "is-good" : ""}`}><span>{photo && placementAssessment.good && !shoulderCrop?.warning ? "✓" : "!"}</span><p><strong>{shoulderCrop?.warning ? "Possible straight side crop" : photo ? placementAssessment.message : "Check the headroom"}</strong>{shoulderCrop?.warning ? "Use a photo without a straight vertical cutoff at the shoulders." : photo ? placementAssessment.detail : "The top of the employee’s head must stay below the thin guide bar."}</p></div><button className="download-button" disabled={!photo || !artwork} onClick={download}>Download PNG <span>↓</span></button></div>
        </section>
      </section>
      {shoulderDialogOpen && <div className="portrait-crop-overlay">
        <section className="portrait-crop-dialog" role="dialog" aria-modal="true" aria-labelledby="shoulder-warning-title" aria-describedby="shoulder-warning-description" onKeyDown={(event) => {
          if (event.key === "Escape") closeShoulderDialog(false);
          if (event.key === "Tab") {
            const buttons = event.currentTarget.querySelectorAll("button");
            if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons[buttons.length - 1]?.focus(); }
            if (!event.shiftKey && document.activeElement === buttons[buttons.length - 1]) { event.preventDefault(); buttons[0]?.focus(); }
          }
        }}>
          <h2 id="shoulder-warning-title">⚠ Shoulder crop warning</h2>
          <p id="shoulder-warning-description">{SHOULDER_CROP_WARNING_TEXT}</p>
          <div className="portrait-crop-dialog-actions">
            <button type="button" autoFocus onClick={() => { closeShoulderDialog(false); fileRef.current?.click(); }}>Replace photo</button>
            <button type="button" className="portrait-crop-continue" onClick={() => closeShoulderDialog(true)}>Yes, proceed anyway</button>
          </div>
        </section>
      </div>}
    </main>
  );
}

export default function App({ directUpdateMode = false }) {
  return <AuthGate>{(auth) => <Studio {...auth} directUpdateMode={directUpdateMode} />}</AuthGate>;
}
